import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material';
import DevSecOpsTabs from '../../components/devsecops/DevSecOpsTabs';
import { useNavigate } from 'react-router-dom';
import DevSecOpsComponentCard, { DevSecOpsComponentDto } from '../../components/devsecops/DevSecOpsComponentCard';
import api, { ApiResponse, authService, User } from '../../services/api';
import { devsecopsApi } from '../../services/devsecopsApi';

const DevSecOpsMetrics: React.FC = () => {
  const navigate = useNavigate();
  const [components, setComponents] = useState<DevSecOpsComponentDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<User['role'] | undefined>(undefined);

  const handleBack = () => {
    navigate('/devsecops');
  };

  const keyMetrics = [
    {
      title: 'Deployment Frequency',
      value: '12.3',
      unit: 'per day',
      target: '10+',
      status: 'success',
      trend: '+15%',
      icon: <SpeedIcon />
    },
    {
      title: 'Lead Time for Changes',
      value: '18',
      unit: 'hours',
      target: '< 24h',
      status: 'success',
      trend: '-22%',
      icon: <TrendingUpIcon />
    },
    {
      title: 'Mean Time to Recovery',
      value: '42',
      unit: 'minutes',
      target: '< 1h',
      status: 'success',
      trend: '-35%',
      icon: <CheckCircleIcon />
    },
    {
      title: 'Change Failure Rate',
      value: '3.2',
      unit: '%',
      target: '< 5%',
      status: 'success',
      trend: '-1.8%',
      icon: <BugReportIcon />
    }
  ];

  const qualityMetrics = [
    { metric: 'Code Coverage', current: 87, target: 80, unit: '%' },
    { metric: 'Build Success Rate', current: 98.5, target: 95, unit: '%' },
    { metric: 'Security Scan Pass Rate', current: 100, target: 100, unit: '%' },
    { metric: '508 Compliance Score', current: 100, target: 100, unit: '%' },
    { metric: 'API Response Time', current: 145, target: 200, unit: 'ms' },
    { metric: 'Frontend Load Time', current: 1.2, target: 2.0, unit: 's' }
  ];

  const securityMetrics = [
    { category: 'Critical Vulnerabilities', count: 0, status: 'success' },
    { category: 'High Vulnerabilities', count: 0, status: 'success' },
    { category: 'Medium Vulnerabilities', count: 3, status: 'warning' },
    { category: 'Low Vulnerabilities', count: 12, status: 'info' },
    { category: 'Secrets Detected', count: 0, status: 'success' },
    { category: 'License Issues', count: 1, status: 'warning' }
  ];

  const recentDeployments = [
    { env: 'Production', version: 'v2.4.1', time: '2h ago', status: 'success', duration: '6m 15s' },
    { env: 'Staging', version: 'v2.5.0-rc1', time: '4h ago', status: 'success', duration: '5m 45s' },
    { env: 'Production', version: 'v2.4.0', time: '1d ago', status: 'success', duration: '6m 30s' },
    { env: 'Staging', version: 'v2.4.0', time: '1d ago', status: 'success', duration: '5m 50s' },
    { env: 'Production', version: 'v2.3.9', time: '2d ago', status: 'success', duration: '6m 20s' }
  ];

  const getProgressColor = (current: number, target: number, isLowerBetter: boolean = false) => {
    if (isLowerBetter) {
      return current <= target ? 'success' : 'warning';
    }
    return current >= target ? 'success' : 'warning';
  };

  const getStatusColor = (status: string): "success" | "error" | "warning" | "info" | "default" => {
    switch (status) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  };

  useEffect(() => {
    const fetchComponents = async () => {
      try {
        setLoading(true);
        const response = await devsecopsApi.get<ApiResponse<DevSecOpsComponentDto[]>>(
          '/devsecops/components',
          {
            params: { page: 'metrics' }
          }
        );

        const data = response.data.data || [];
        setComponents(data);
        setError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to load metrics components';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchComponents();
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const user = await authService.getCurrentUser();
        setCurrentUserRole(user.role);
      } catch (_err) {
        // unauthenticated users will see read-only cards
      }
    };

    loadUser();
  }, []);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to DevSecOps Program"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            DevSecOps Metrics Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Real-Time Performance, Quality, and Security Metrics
          </Typography>
        </Box>
      </Box>

      {/* DevSecOps Tabs */}
      <DevSecOpsTabs activeTab="metrics" />

      {/* Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Metrics Overview
          </Typography>
          <Typography variant="body1">
            Track key DevSecOps performance indicators aligned with DORA metrics and ODOS IV requirements.
            All metrics are updated in real-time and reported monthly to program stakeholders.
          </Typography>
        </CardContent>
      </Card>

      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mb: 3 }}>
        Metrics & SLO Capabilities
      </Typography>

      {loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading DevSecOps metrics components...
        </Typography>
      )}

      {error && (
        <Typography variant="body2" color="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {components.map((component: DevSecOpsComponentDto) => (
          <Grid item xs={12} md={6} key={component.key}>
            <DevSecOpsComponentCard
              component={component}
              currentUserRole={currentUserRole}
              onUpdated={(updated) => {
                setComponents((prev: DevSecOpsComponentDto[]) =>
                  prev.map((c: DevSecOpsComponentDto) => (c.key === updated.key ? updated : c))
                );
              }}
            />
          </Grid>
        ))}
      </Grid>

      {/* DORA Metrics */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mb: 3 }}>
        DORA Metrics (Elite Performers)
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {keyMetrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ color: '#2E7D32', mr: 2 }}>
                    {metric.icon}
                  </Box>
                  <Typography variant="h6" fontWeight="bold">
                    {metric.title}
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight="bold" color="primary">
                  {metric.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {metric.unit}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <Chip
                    label={`Target: ${metric.target}`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                  <Chip
                    label={metric.trend}
                    size="small"
                    color={metric.trend.startsWith('+') ? 'success' : 'error'}
                    icon={metric.trend.startsWith('+') ? <TrendingUpIcon /> : <TrendingDownIcon />}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quality Metrics */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
            Quality & Performance Metrics
          </Typography>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {qualityMetrics.map((metric, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" fontWeight="medium">
                      {metric.metric}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {metric.current}{metric.unit} / {metric.target}{metric.unit}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min((metric.current / metric.target) * 100, 100)}
                    color={getProgressColor(
                      metric.current,
                      metric.target,
                      metric.metric.includes('Time')
                    )}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Security Metrics */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <SecurityIcon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
            <Typography variant="h5" color="primary" fontWeight="bold">
              Security Scan Results
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {securityMetrics.map((metric, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    p: 2,
                    border: '2px solid',
                    borderColor: metric.status === 'success' ? 'success.main' : 'warning.main',
                    borderRadius: 1,
                    bgcolor: metric.status === 'success' ? 'success.light' : 'warning.light',
                    opacity: 0.9
                  }}
                >
                  <Typography variant="caption" color="text.secondary" display="block">
                    {metric.category}
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {metric.count}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Recent Deployments */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
            Recent Deployments
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Environment</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Version</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Time</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Duration</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentDeployments.map((deployment, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Chip
                        label={deployment.env}
                        size="small"
                        color={deployment.env === 'Production' ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>{deployment.version}</TableCell>
                    <TableCell>{deployment.time}</TableCell>
                    <TableCell>{deployment.duration}</TableCell>
                    <TableCell>
                      <Chip
                        label={deployment.status}
                        size="small"
                        color={getStatusColor(deployment.status)}
                        icon={<CheckCircleIcon />}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Container>
  );
};

export default DevSecOpsMetrics;
